import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import {
    getFoodCategories,
    getProducts,
    saveOrder,
    updateKOT,
    deleteKOT,
    voiceOrder,
    getExistingKOTs,
    //getAggregatedKOTs
} from "../api/orderApi";

import KOTPrintLayout from "../components/KOTPrintLayout";
import config from "../config";

export default function OrderPage() {
    const { tableId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const restaurantId = user?.restaurantId;
    const dashboardCategory = location.state?.selectedCategory || tableId[0];

    const [isProductDisplay, setProductDisplay] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [existingKOTs, setExistingKOTs] = useState([]);
    const [selectedKOTId, setSelectedKOTId] = useState("all");
    const [pendingOrder, setPendingOrder] = useState([]); // ✅ for voice confirmation
    const [listening, setListening] = useState(false);
    const [billData] = useState(null);
    const [showBillModal, setShowBillModal] = useState(false);

    const ranRef = useRef(false);
    const actionInFlight = useRef(false);
    const [showCartMobile, setShowCartMobile] = useState(false);
    const [displayGenerateBill, setDisplayGenerateBill] = useState(false);
    const [processingAction, setProcessingAction] = useState("");

    const preselectedKOTId = location.state?.selectedKOTId || null;

    const selectedKOT = existingKOTs.find((kot) => String(kot.kot_id) === selectedKOTId);
    const isEditingExistingKOT = selectedKOTId !== "all";

    // Controls what is shown in the cart based on the dropdown filter
    const visibleCart = isEditingExistingKOT
        ? cart.filter((item) => item.isExisting && String(item.kotId) === selectedKOTId)
        : cart;
    const visibleCartTotal = visibleCart.reduce((sum, item) => sum + item.qty * item.price, 0);
    const visibleCartQty = visibleCart.reduce((sum, item) => sum + item.qty, 0);
    const showKOTActionControls = visibleCart.length > 0 || isEditingExistingKOT;

    // ✅ Add this mapping block to construct the structure expected by KOTPrintLayout
    const activePrintData = selectedKOT ? {
        kotId: selectedKOT.kot_id,
        seatingName: tableId,
        products: visibleCart.map(item => ({
            productId: item.pid,
            name: item.name,
            quantity: item.qty,
            price: item.price
        })),
        createdBy: selectedKOT.createdBy || user?.name || "System",
        createdAt: selectedKOT.createdAt || new Date().toISOString(),
        timestamp: new Date().toISOString()
    } : null;



    const mapKOTsToCartItems = (kots) => {
        const items = [];
        kots.forEach((kot) => {
            kot.products.forEach((product) => {
                items.push({
                    ...product,
                    pid: product.productId,
                    qty: product.quantity,
                    isExisting: true,
                    kotId: kot.kot_id,
                });
            });
        });
        return items;
    };

    // ✅ Load categories once
    useEffect(() => {
        if (ranRef.current) return;
        if (!restaurantId) return;
        ranRef.current = true;

        const loadCategories = async () => {
            const data = await getFoodCategories(restaurantId);
            setCategories(data);
        };

        loadCategories();
    }, [restaurantId]);

    // ✅ Load existing KOTs on mount
    useEffect(() => {
        if (!tableId || !restaurantId) return;

        const loadExistingKOTs = async () => {
            try {
                const kots = await getExistingKOTs(tableId, restaurantId);
                setExistingKOTs(kots);
                if (kots.length > 0 && user.role === config.USER_MANAGER) {
                    setDisplayGenerateBill(true);
                }

                const existingItems = mapKOTsToCartItems(kots);

                setCart((prev) => {
                    const newItemsOnly = prev.filter(item => !item.isExisting);
                    return [...existingItems, ...newItemsOnly];
                });
            } catch (error) {
                console.error("Failed to load existing KOTs:", error);
            }
        };

        loadExistingKOTs();
    }, [tableId, restaurantId, user]);

    useEffect(() => {
        if (!preselectedKOTId || existingKOTs.length === 0) return;

        const exists = existingKOTs.some((kot) => String(kot.kot_id) === String(preselectedKOTId));
        if (exists) {
            setSelectedKOTId(String(preselectedKOTId));
        }
    }, [preselectedKOTId, existingKOTs]);

    // ✅ Load products
    useEffect(() => {
        if (!restaurantId || !selectedCategory) {
            setProducts([]);
            return;
        }

        const loadProducts = async () => {
            try {
                const data = await getProducts(selectedCategory, restaurantId);
                const productsWithPid = (data || []).map((p) => ({
                    ...p,
                    pid: p.pid ?? p.p_id ?? p.product_id ?? p.id,
                }));

                productsWithPid.sort((a, b) => (a.preference_order || 0) - (b.preference_order || 0));

                setProducts(productsWithPid);
            } catch (error) {
                console.error("Failed to load products:", error);
                setProducts([]);
            }
        };

        loadProducts();
    }, [selectedCategory, restaurantId]);

    // ✅ Add to cart (merge qty)
    const addToCart = (item) => {
        console.log("Adding to cart:", item);
        setCart((prev) => {
            if (isEditingExistingKOT) {
                const existing = prev.find(
                    (i) => i.pid === item.pid && i.isExisting && String(i.kotId) === selectedKOTId
                );

                if (existing) {
                    return prev.map((i) =>
                        i.pid === item.pid && i.isExisting && String(i.kotId) === selectedKOTId
                            ? { ...i, qty: i.qty + 1, quantity: i.qty + 1 }
                            : i
                    );
                }

                return [
                    ...prev,
                    {
                        ...item,
                        pid: item.pid,
                        productId: item.pid,
                        qty: 1,
                        quantity: 1,
                        gst: Number(item.gst ?? item.effectiveGSTinPer ?? item.GSTinPer) || 0,
                        isExisting: true,
                        kotId: selectedKOTId,
                    },
                ];
            }

            const existing = prev.find((i) => i.pid === item.pid && !i.isExisting);

            if (existing) {
                return prev.map((i) =>
                    i.pid === item.pid && !i.isExisting ? { ...i, qty: i.qty + 1 } : i
                );
            }

            return [...prev, {
                ...item,
                qty: 1,
                gst: Number(item.effectiveGSTinPer ?? item.GSTinPer) || 0,
                isExisting: false
            }];
        });
    };

    const increaseQty = (item) => {
        setCart((prev) =>
            prev.map((i) =>
                i.pid === item.pid && String(i.kotId ?? "") === String(item.kotId ?? "") && i.isExisting === item.isExisting
                    ? { ...i, qty: i.qty + 1, quantity: i.qty + 1 }
                    : i
            )
        );
    };

    const decreaseQty = (item) => {
        setCart((prev) =>
            prev
                .map((i) =>
                    i.pid === item.pid && String(i.kotId ?? "") === String(item.kotId ?? "") && i.isExisting === item.isExisting
                        ? { ...i, qty: i.qty - 1, quantity: i.qty - 1 }
                        : i
                )
                .filter((i) => i.qty > 0)
        );
    };

    const removeItem = (item) => {
        setCart((prev) =>
            prev.filter(
                (cartItem) =>
                    !(
                        cartItem.pid === item.pid &&
                        String(cartItem.kotId ?? "") === String(item.kotId ?? "") &&
                        cartItem.isExisting === item.isExisting
                    )
            )
        );
    };

    const buildKOTPayloadItems = (items) =>
        items.map((item) => ({
            productId: item.pid,
            name: item.name,
            price: Number(item.price),
            qty: Number(item.qty),
            gst: Number(item.gst ?? item.effectiveGSTinPer ?? item.GSTinPer) || 0,
        }));

    const handleReprintKOT = async () => {
        if (!isEditingExistingKOT) {
            if (!activePrintData) return;
            window.print();
            return;
        }

        if (actionInFlight.current) return;
        actionInFlight.current = true;
        setProcessingAction("reprint");

        const kotItems = cart.filter((item) => String(item.kotId) === selectedKOTId);

        try {
            if (kotItems.length === 0) {
                const confirmDelete = window.confirm("No items left in this KOT. Would you like to delete this KOT completely?");
                if (!confirmDelete) return;

                await deleteKOT(selectedKOTId, restaurantId);
                alert("KOT deleted successfully!");
                navigate(`/dashboard`, { state: { selectedCategory: dashboardCategory } });
                return;
            }

            const payloadItems = buildKOTPayloadItems(kotItems);

            await updateKOT(selectedKOTId, {
                items: payloadItems,
                kotItems: payloadItems,
                restaurantId
            });
            window.print();
        } catch (error) {
            console.error("Failed to process KOT before print:", error);
            alert("Failed to process KOT before print");
        } finally {
            actionInFlight.current = false;
            setProcessingAction("");
        }
    };

    // ✅ Save KOT / Update KOT
    const handleSaveOrder = async () => {
        if (actionInFlight.current) return;
        actionInFlight.current = true;
        setProcessingAction("kot");

        let failureMessage = isEditingExistingKOT ? "Failed to update KOT" : "Failed to save KOT";

        try {
            if (isEditingExistingKOT) {
                const kotItems = cart.filter((item) => String(item.kotId) === selectedKOTId);

                if (kotItems.length === 0) {
                    const confirmDelete = window.confirm("No items left in this KOT. Would you like to delete this KOT completely?");
                    if (!confirmDelete) return;

                    failureMessage = "Failed to delete KOT";
                    await deleteKOT(selectedKOTId, restaurantId);
                    alert("KOT deleted successfully!");
                    navigate(`/dashboard`, { state: { selectedCategory: dashboardCategory } });
                    return;
                }

                const payloadItems = buildKOTPayloadItems(kotItems);

                await updateKOT(selectedKOTId, {
                    items: payloadItems,
                    kotItems: payloadItems,
                    restaurantId
                });
                alert("KOT updated successfully!");
                navigate(`/dashboard`, { state: { selectedCategory: dashboardCategory } });
            } else {
                const newItems = cart.filter((item) => !item.isExisting);

                if (newItems.length === 0) {
                    alert("Please add new items to create a KOT");
                    return;
                }

                const kotItems = newItems.map((item) => ({
                    productId: item.pid,
                    name: item.name,
                    price: Number(item.price),
                    qty: Number(item.qty),
                    gst: Number(item.effectiveGSTinPer ?? item.GSTinPer) || 0,
                }));

                await saveOrder({
                    restaurantId,
                    tableId,
                    items: kotItems,
                    kotItems,
                    createdAt: new Date().toISOString(),
                    role: user.role,
                    createdBy: user.id
                });
                if (user.role === config.USER_WAITER) {
                    alert("New KOT created successfully!");
                }
                navigate(`/dashboard`, { state: { selectedCategory: dashboardCategory } });
            }
        } catch (error) {
            console.error("Failed to save or update KOT:", error);
            alert(failureMessage);
        } finally {
            actionInFlight.current = false;
            setProcessingAction("");
        }
    };

    // ✅ Generate Bill
    const handleGenerateBill = async () => {
        if (!tableId) {
            alert("Please select a table and restaurant to generate bill");
            return;
        }
        if (actionInFlight.current) return;
        actionInFlight.current = true;
        setProcessingAction("bill");
        navigate(`/bill/${encodeURIComponent(tableId)}`, { state: { selectedCategory: dashboardCategory } });
    };

    // ✅ Voice → Backend → AI processing
    const processVoiceCommand = async (text) => {
        try {
            const data = await voiceOrder(text);
            console.log("AI response:", data);
            setPendingOrder(data);
        } catch (err) {
            console.error("Voice error:", err);
        }
    };

    // ✅ Start voice
    const startListening = () => {
        setListening(true);
        const SpeechRecognition =
            window.SpeechRecognition || window.webkitSpeechRecognition;

        if (!SpeechRecognition) {
            alert("Speech not supported");
            return;
        }

        const recognition = new SpeechRecognition();
        recognition.lang = "en-US";

        recognition.onresult = (event) => {
            const speechText = event.results[0][0].transcript;
            console.log("User said:", speechText);
            setListening(false);
            processVoiceCommand(speechText);
        };

        recognition.start();
    };

    return (
        <div className="pb-32 md:pb-20 lg:pb-0 bg-gray-50 min-h-[calc(100vh-178px)] md:min-h-[calc(100vh-178px)] lg:min-h-[calc(100vh-178px)]">
            {/* ✅ Mount the print component at the root wrapper */}
            <KOTPrintLayout data={activePrintData} user={user} />
            {/* Header */}
            <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate('/dashboard')}
                            className="bg-rose-600 text-gray-800 px-3 py-2 rounded shadow-sm hover:bg-gray-200 transition"
                        >
                            ← Exit
                        </button>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Table: {tableId}</h2>
                            <p className="text-xs text-gray-500">Restaurant ID: {restaurantId || '—'}</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={startListening}
                            disabled={listening}
                            className={`hidden sm:inline-flex items-center gap-2 ${listening ? 'bg-red-500' : 'bg-green-500 hover:bg-green-600'} text-white px-4 py-2 rounded-lg shadow transition`}
                        >
                            🎤 {listening ? 'Listening...' : 'Voice Order'}
                        </button>

                        <button
                            onClick={startListening}
                            disabled={listening}
                            className={`sm:hidden ${listening ? 'bg-red-500 animate-pulse' : 'bg-green-500'} text-white p-3 rounded-full shadow-lg transition`}
                            aria-label="Start voice order"
                        >
                            🎤
                        </button>
                    </div>
                </div>
            </div>

            {/* Pending voice confirmation */}
            {pendingOrder.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 py-3 mt-3">
                    <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300 shadow-md">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-bold text-yellow-900">✨ Confirm Voice Order</h3>
                            <button
                                onClick={() => setPendingOrder([])}
                                className="text-sm text-yellow-700 hover:text-yellow-900 font-semibold"
                            >
                                Discard
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                            {pendingOrder.map((item, idx) => (
                                <div key={item.pid ?? `${item.name}-${idx}`} className="bg-white p-2 rounded shadow-sm border border-yellow-200">
                                    <div className="font-medium text-sm">{item.name}</div>
                                    <div className="text-xs text-gray-600">× {item.qty}</div>
                                </div>
                            ))}
                        </div>

                        <button
                            onClick={() => {
                                pendingOrder.forEach((item) => {
                                    for (let i = 0; i < item.qty; i++) {
                                        addToCart(item);
                                    }
                                });
                                setPendingOrder([]);
                            }}
                            className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition"
                        >
                            ✅ Add to Cart
                        </button>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Tablet Cart Bar */}
                {showKOTActionControls && (
                    <div className="hidden md:flex lg:hidden bg-gradient-to-r from-indigo-600 to-indigo-500 text-white p-4 rounded-lg mb-6 shadow-lg items-center justify-between">
                        <div>
                            <div className="text-sm opacity-90">{visibleCartQty} item(s) in active view</div>
                            <div className="text-xl font-bold">₹{visibleCartTotal}</div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowCartMobile(true)}
                                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition"
                            >
                                View Cart
                            </button>

                            <button
                                onClick={handleSaveOrder}
                                disabled={Boolean(processingAction)}
                                className="bg-green-500 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold transition shadow-lg"
                            >
                                {processingAction === "kot" ? "Processing..." : isEditingExistingKOT ? "Update KOT" : "Save KOT"}
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
                    {/* Desktop Cart Sidebar */}
                    <div className="hidden lg:block lg:col-span-2">
                        <div className="sticky top-24 bg-white p-5 rounded-xl shadow-lg border border-gray-200">
                            <h3 className="font-bold text-xs mb-3 text-gray-900">🛒 Cart</h3>

                            {/* Dynamic KOT Dropdown */}
                            <select
                                value={selectedKOTId}
                                onChange={(e) => setSelectedKOTId(e.target.value)}
                                className="w-full p-2 mb-4 border rounded-lg bg-gray-50 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="all">All KOTs / New Items</option>
                                {existingKOTs.map((kot) => (
                                    <option key={kot.kot_id} value={String(kot.kot_id)}>
                                        KOT #{kot.kot_id} ({kot.status || 'Active'})
                                    </option>
                                ))}
                            </select>

                            {visibleCart.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-8">No items in this view</p>
                            ) : (
                                <>
                                    <div className="space-y-1 max-h-[45vh] overflow-y-auto mb-4">
                                        {visibleCart.map((item, idx) => (
                                            <div
                                                key={item.isExisting ? `${item.pid}-${item.kotId}` : (item.pid ?? `cart-${item.name}-${idx}`)}
                                                className={`flex justify-between items-center p-1 rounded-lg border ${item.isExisting
                                                    ? "border-blue-200"
                                                    : "border-gray-200"
                                                    }`}
                                            >
                                                <div className="flex-1">
                                                    <div className="font-medium text-sm text-gray-900">{item.name}</div>
                                                    {item.isExisting && (
                                                        <div className="text-xs font-semibold pr-1"><small>Kot id: {item.kotId} </small></div>
                                                    )}
                                                </div>
                                                <div className="text-xs mr-2">₹{item.price}</div>
                                                <div className="flex items-center gap-2 ml-2">
                                                    {(!item.isExisting || isEditingExistingKOT) ? (
                                                        <>
                                                            <button
                                                                onClick={() => decreaseQty(item)}
                                                                className="bg-gray-200 hover:bg-gray-300 px-2 py-0.5 rounded text-sm transition"
                                                            >
                                                                −
                                                            </button>
                                                            <span className="w-6 text-center font-semibold text-sm">{item.qty}</span>
                                                            <button
                                                                onClick={() => increaseQty(item)}
                                                                className="bg-gray-200 hover:bg-gray-300 px-2 py-0.5 rounded text-sm transition"
                                                            >
                                                                +
                                                            </button>
                                                            <button
                                                                onClick={() => removeItem(item)}
                                                                className="text-red-500 hover:text-red-700 ml-1 text-lg"
                                                                title="Remove"
                                                            >
                                                                ✕
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <span className="w-6 text-center font-semibold text-xs">{item.qty}</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                </>
                            )}
                            {showKOTActionControls && (

                                <div className="border-t border-gray-200 pt-3">
                                    <div className="flex justify-between mb-2">
                                        <span className="text-sm text-gray-600">Total Items:</span>
                                        <span className="font-bold">{visibleCartQty}</span>
                                    </div>
                                    <div className="flex justify-between mb-4">
                                        <span className="text-sm text-gray-600">Total:</span>
                                        <span className="font-bold text-sm">₹{visibleCartTotal}</span>
                                    </div>
                                    <div className="space-y-2">
                                        <button
                                            onClick={handleSaveOrder}
                                            disabled={Boolean(processingAction)}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-2 py-1 rounded-lg font-bold transition shadow-md"
                                        >
                                            {processingAction === "kot" ? "Processing..." : isEditingExistingKOT ? "Update KOT 📋" : "Save KOT 📋"}
                                        </button>

                                        {isEditingExistingKOT ? (
                                            <button
                                                onClick={handleReprintKOT}
                                                disabled={Boolean(processingAction)}
                                                className="w-full bg-amber-600 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-2 py-1 rounded-lg font-bold transition shadow-md"
                                            >
                                                {processingAction === "reprint" ? "Processing..." : "Reprint KOT 🖨️"}
                                            </button>
                                        ) : (
                                            displayGenerateBill && (
                                                <button
                                                    onClick={handleGenerateBill}
                                                    disabled={Boolean(processingAction)}
                                                    className="w-full bg-green-600 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-2 py-1 rounded-lg font-bold transition shadow-md"
                                                >
                                                    {processingAction === "bill" ? "Processing..." : "Generate Bill 💳"}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Main Content Area */}
                    <div className="md:col-span-3 lg:col-span-3 text-xs">
                        {/* CATEGORIES VIEW */}
                        {!isProductDisplay ? (
                            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                <h2 className="text-2xl font-bold mb-6 text-gray-900">Select a Category</h2>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {categories.map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => {
                                                setSelectedCategory(cat.id);
                                                setProductDisplay(true);
                                            }}
                                            className="group p-2 rounded-xl border-2 border-gray-200 hover:border-indigo-500 bg-white hover:bg-indigo-50 transition shadow-sm hover:shadow-md"
                                        >
                                            <div
                                                className="w-12 h-12 rounded-full mx-auto mb-3 shadow-md group-hover:scale-110 transition"
                                                style={{ background: cat.colorcode || '#eee' }}
                                            />
                                            <div className="font-semibold text-gray-900 text-sm line-clamp-2">{cat.name}</div>
                                            {cat.preference_order && (
                                                <div className="text-xs text-gray-500 mt-1">#{cat.preference_order}</div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            /* PRODUCTS VIEW */
                            <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
                                <div className="flex items-center justify-between mb-6 sticky top-[70px]">
                                    <div>
                                        <h2 className="text-2xl font-bold text-gray-900">
                                            {categories.find((c) => c.id === selectedCategory)?.name || 'Products'}
                                        </h2>
                                        <p className="text-sm text-gray-500 mt-1">{products.length} items available</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setProductDisplay(false);
                                            setSelectedCategory("");
                                            setProducts([]);
                                        }}
                                        className="bg-indigo-400 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold transition"
                                    >
                                        ← Back to Categories
                                    </button>
                                </div>

                                {products.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500 text-lg">No products available in this category</p>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                        {products.map((p, idx) => (
                                            <button
                                                key={p.pid ?? `prod-${p.name}-${idx}`}
                                                onClick={() => addToCart(p)}
                                                className="group p-4 rounded-xl from-gray-50 to-white border-2 border-gray-200 hover:border-green-500 hover:from-green-50 hover:to-white transition shadow-sm hover:shadow-lg text-left"
                                            >
                                                <div className="font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-green-700 transition leading-5">
                                                    {p.name}
                                                </div>
                                                <div className="flex items-end justify-between">
                                                    <div>
                                                        <div className="text-xs font-bold text-green-600">₹{p.price}</div>
                                                        {(p.effectiveGSTinPer ?? p.GSTinPer) != null && (
                                                            <div className="text-xs text-gray-500">GST {p.effectiveGSTinPer ?? p.GSTinPer}%</div>
                                                        )}
                                                    </div>
                                                    <div className="bg-green-500 group-hover:bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold transition shadow-md">
                                                        +
                                                    </div>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile & Tablet Floating Bar */}
            {(cart.length > 0 || isEditingExistingKOT) && (
                <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-indigo-500 text-white p-4 shadow-2xl z-50 border-t-2 border-indigo-700">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="text-sm opacity-90">{visibleCartQty} item(s) in view</div>
                            <div className="text-xl font-bold">₹{visibleCartTotal}</div>
                        </div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowCartMobile(true)}
                                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition"
                            >
                                View
                            </button>
                            <button
                                onClick={handleSaveOrder}
                                disabled={Boolean(processingAction)}
                                className="bg-green-500 hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold transition shadow-lg"
                            >
                                {processingAction === "kot" ? "Processing..." : isEditingExistingKOT ? "Update" : "Save"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile & Tablet Cart Modal */}
            {showCartMobile && (
                <div className="lg:hidden fixed inset-0 bg-black/60 z-50 flex items-end">
                    <div className="bg-white w-full rounded-t-2xl p-4 max-h-[75vh] overflow-auto">
                        <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 pb-3">
                            <h3 className="text-xl font-bold text-gray-900">🛒 Your Cart</h3>
                            <button
                                onClick={() => setShowCartMobile(false)}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ✕
                            </button>
                        </div>

                        {visibleCart.length === 0 ? (
                            <>
                                <p className="text-center text-gray-500 py-8">No items in this view</p>
                                {isEditingExistingKOT && (
                                    <div className="border-t pt-4">
                                        <div className="space-y-2">
                                            <button
                                                onClick={handleSaveOrder}
                                                disabled={Boolean(processingAction)}
                                                className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-3 rounded-lg font-bold transition shadow-lg"
                                            >
                                                {processingAction === "kot" ? "Processing..." : "Update KOT"}
                                            </button>
                                            <button
                                                onClick={handleReprintKOT}
                                                disabled={Boolean(processingAction)}
                                                className="w-full bg-amber-600 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-3 rounded-lg font-bold transition shadow-lg"
                                            >
                                                {processingAction === "reprint" ? "Processing..." : "Reprint KOT"}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div className="space-y-3 mb-4">
                                    {visibleCart.map((item, idx) => (
                                        <div
                                            key={item.isExisting ? `${item.pid}-${item.kotId}` : (item.pid ?? `cart-mobile-${idx}`)}
                                            className="flex justify-between items-center px-3 py-1 bg-gray-100 rounded-lg"
                                        >
                                            <div>
                                                <div className="font-bold text-gray-900">{item.name}</div>
                                                <div className="text-xs text-gray-600">₹{item.price}</div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {(!item.isExisting || isEditingExistingKOT) ? (
                                                    <>
                                                        <button
                                                            onClick={() => decreaseQty(item)}
                                                            className="bg-gray-300 px-2 py-1 rounded font-bold"
                                                        >
                                                            −
                                                        </button>
                                                        <span className="w-6 text-center font-bold">{item.qty}</span>
                                                        <button
                                                            onClick={() => increaseQty(item)}
                                                            className="bg-gray-300 px-2 py-1 rounded font-bold"
                                                        >
                                                            +
                                                        </button>
                                                        <button
                                                            onClick={() => removeItem(item)}
                                                            className="text-red-600 ml-2 font-bold text-lg"
                                                        >
                                                            ✕
                                                        </button>
                                                    </>
                                                ) : (
                                                    <span className="w-6 text-center font-bold text-blue-600">{item.qty}</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="border-t pt-4">
                                    <div className="flex justify-between mb-3 font-bold text-lg">
                                        <span>Total:</span>
                                        <span className="text-indigo-600">₹{visibleCartTotal}</span>
                                    </div>
                                    <div className="space-y-2">
                                        <button
                                            onClick={handleSaveOrder}
                                            disabled={Boolean(processingAction)}
                                            className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-3 rounded-lg font-bold transition shadow-lg"
                                        >
                                            {processingAction === "kot" ? "Processing..." : isEditingExistingKOT ? "Update KOT 📋" : "Save KOT 📋"}
                                        </button>
                                        {isEditingExistingKOT ? (
                                            <button
                                                onClick={handleReprintKOT}
                                                disabled={Boolean(processingAction)}
                                                className="w-full bg-amber-600 hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-3 rounded-lg font-bold transition shadow-lg"
                                            >
                                                {processingAction === "reprint" ? "Processing..." : "Reprint KOT"}
                                            </button>
                                        ) : (
                                            (displayGenerateBill && user.role === "admin") && (
                                                <button
                                                    onClick={handleGenerateBill}
                                                    disabled={Boolean(processingAction)}
                                                    className="w-full bg-green-600 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 text-white px-4 py-3 rounded-lg font-bold transition shadow-lg"
                                                >
                                                    {processingAction === "bill" ? "Processing..." : "Generate Bill 💳"}
                                                </button>
                                            )
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Bill Modal */}
            {showBillModal && billData && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-lg max-h-[80vh] overflow-auto shadow-2xl">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-sm font-bold text-red-900">💳 Bill Summary</h2>
                            <button
                                onClick={() => setShowBillModal(false)}
                                className="text-red-500 hover:text-red-700 text-xs"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="text-sm text-gray-600 mb-3">
                                <div>Table: <span className="font-bold">{tableId}</span></div>
                                <div>Total KOTs: <span className="font-bold">{billData.kotCount}</span></div>
                            </div>
                        </div>

                        <div className="mb-2">
                            <h3 className="font-bold text-xs mb-1 text-gray-900">Items:</h3>
                            <div className="space-y-1 max-h-[30vh] overflow-y-auto">
                                {billData.products.map((item, idx) => (
                                    <div key={`bill-item-${idx}`} className="flex justify-between text-sm p-2 bg-gray-50 rounded">
                                        <div>
                                            <div className="font-medium text-xs text-gray-900">{item.name}</div>
                                            <div className="text-xs text-gray-600">₹{item.price} × {item.quantity}</div>
                                        </div>
                                        <div className="text-xs font-bold text-gray-900">₹{(item.price * item.quantity).toFixed(2)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="border-t pt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">Subtotal:</span>
                                <span className="font-bold">₹{billData.totals.subTotal.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-gray-600">GST:</span>
                                <span className="font-bold">₹{billData.totals.totalGst.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between text-lg font-bold bg-green-50 p-3 rounded mt-3">
                                <span>Total Amount:</span>
                                <span className="text-green-600">₹{billData.totals.totalAmount.toFixed(2)}</span>
                            </div>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <button
                                onClick={() => setShowBillModal(false)}
                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-bold transition"
                            >
                                Close
                            </button>
                            <button
                                onClick={() => alert("Bill generation complete. Proceed to payment.")}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold transition"
                            >
                                Print Bill
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}